import { Injectable, inject } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';
import { AgentWorkbenchDto, AgentWorkbenchService } from '../agent-workbench.service';
import {
  AGENT_MVP_CATEGORY_ORDER,
  ActionCategoryDto,
  compareAgentCases,
} from '../models/agent-workbench.model';
import { MockDataLoader } from './mock-data-loader.service';
import { ActionMockStateService } from './action-mock-state.service';
import { MockAccessScopeService } from './mock-access-scope.service';

@Injectable()
export class AgentWorkbenchMockService extends AgentWorkbenchService {
  private readonly mockData = inject(MockDataLoader);
  private readonly actionState = inject(ActionMockStateService);
  private readonly accessScope = inject(MockAccessScopeService);

  getWorkbench(): Observable<AgentWorkbenchDto> {
    return this.mockData.load<AgentWorkbenchDto>('agent-workbench').pipe(
      switchMap(workbench => this.mockData.load<{ items: Array<{ caseId: string; agentId?: string; creditorId?: string }> }>('collection-cases').pipe(
        map(cases => this.normalizeWorkbench(workbench, new Map(cases.items.map(item => [item.caseId, item])))),
      )),
    );
  }

  private normalizeWorkbench(workbench: AgentWorkbenchDto, caseIndex: Map<string, { agentId?: string; creditorId?: string }>): AgentWorkbenchDto {
    const categoryOrder = new Map<string, number>(
      AGENT_MVP_CATEGORY_ORDER.map((code, index) => [code, index]),
    );
    const categories = workbench.categories
      .map(category => this.normalizeCategory({
        ...category,
        cases: category.cases
          .filter(item => this.accessScope.canAccessCase(caseIndex.get(item.caseId) ?? { agentId: 'AGT-001' }))
          .map(item => {
          const last = this.actionState.getLastForCase(item.caseId);
          return last ? { ...item, lastActionLabel: this.actionState.toInteraction(last).actionLabel } : item;
          }),
      }))
      .sort((a, b) =>
        (categoryOrder.get(a.categoryCode) ?? Number.MAX_SAFE_INTEGER) -
        (categoryOrder.get(b.categoryCode) ?? Number.MAX_SAFE_INTEGER),
      );
    const allCases = categories.flatMap(category => category.cases);

    return {
      header: {
        ...workbench.header,
        totalAssignedCases: allCases.length,
        totalCasesToProcess: allCases.filter(item => item.status !== 'SUSPENDED').length,
        portfolioOverdueAmount: allCases.reduce((sum, item) => sum + item.overdueAmount, 0),
      },
      categories,
    };
  }

  private normalizeCategory(category: ActionCategoryDto): ActionCategoryDto {
    const cases = [...category.cases].sort(compareAgentCases);
    return {
      ...category,
      cases,
      caseCount: cases.length,
      totalAmount: cases.reduce((sum, item) => sum + item.overdueAmount, 0),
      oldestAgeDays: cases[0]?.categoryAgeDays ?? 0,
    };
  }
}
